import type { FC } from 'react';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import MyLocationOutlined from '@mui/icons-material/MyLocationOutlined';
import SignalCellularAltOutlined from '@mui/icons-material/SignalCellularAltOutlined';
import { Box, Grid, Stack, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { upperFirst } from 'lodash';

import type {
  Filter,
  Inspiration,
  InspirationContent,
} from 'src/types/followUpInspirations';
import ContentTextField from 'src/components/followUpInspirations/components/InspirationsPage/components/ContentTextField';
import KpiPointsWidget from 'src/components/followUpInspirations/components/InspirationsPage/components/KpiPointsWidget';
import {
  FILTER_GROUP_KPI,
  FILTER_GROUP_SCORE,
  FILTER_GROUP_TEAM_SIZE,
} from 'src/components/followUpInspirations/constants';

import { styles } from './index.styles';

export const PANEL_CONTENT_KPI_FOOTER_TEST_ID = 'panel-content-kpi-footer';
export const PANEL_CONTENT_STATS_TEST_ID = 'panel-content-stats';

interface PanelContentProps {
  inspiration: Inspiration;
  filterResult: { [key: string]: Filter };
  // the t function has to be passed as a prop because useTranslation triggers a re-rendering
  // on first mount which interferes with the scroll into view event of the parent component
  t: TFunction;
}

const PanelContent: FC<PanelContentProps> = ({
  inspiration,
  filterResult,
  t,
}) => {
  const selectedTeamSizeFilter = filterResult[FILTER_GROUP_TEAM_SIZE];
  const selectedKpiFilter = filterResult[FILTER_GROUP_KPI];
  const selectedScoreFilter = filterResult[FILTER_GROUP_SCORE];
  const inspirationContent: InspirationContent =
    inspiration.content as InspirationContent;

  if (
    !selectedTeamSizeFilter ||
    !selectedKpiFilter ||
    !selectedScoreFilter ||
    !inspirationContent?.title ||
    !inspiration?.values
  ) {
    return null;
  }

  const kpiAttr = [
    {
      icon: <GroupsOutlined />,
      attr: selectedTeamSizeFilter.label,
    },
    { icon: <LocationOnOutlined />, attr: selectedKpiFilter.label },
    {
      icon: <InsightsOutlined />,
      attr: selectedScoreFilter.label,
    },
  ];

  const stats = [
    {
      icon: <CategoryOutlined />,
      title: t('followUpInspirations:category'),
      value: inspirationContent?.category,
    },
    {
      icon: <MyLocationOutlined />,
      title: t('followUpInspirations:target'),
      value: inspirationContent?.target,
    },
    {
      icon: <SignalCellularAltOutlined />,
      title: t('followUpInspirations:effort'),
      value: inspirationContent?.effort,
    },
  ];

  return (
    <Grid container rowSpacing={1.25} columnSpacing={1.25}>
      <Grid item xs={12}>
        <Typography variant="h6" sx={styles.tabHeadline}>
          {`${t('followUpInspirations:panelTitlePrefix')}: ${
            inspirationContent?.title
          }`}
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box sx={styles.contentBox}>
          <KpiPointsWidget
            inspirationValues={[...inspiration.values]}
            highlightedKpi={selectedKpiFilter}
            t={t}
          />
          <Stack
            direction="row"
            sx={styles.kpiTableFooter}
            data-test-id={PANEL_CONTENT_KPI_FOOTER_TEST_ID}
            spacing={1}
          >
            {kpiAttr.map(({ icon, attr }, i) => (
              <Stack
                direction="row"
                key={i}
                sx={{
                  py: 2,
                  flex: 1,
                  justifyContent: 'center',
                }}
              >
                {icon}
                <Typography
                  component="span"
                  sx={{
                    pl: 2,
                  }}
                >
                  {attr}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Stack
          direction="column"
          spacing={1.25}
          sx={{
            height: '100%',
          }}
        >
          <Stack
            direction="row"
            data-test-id={PANEL_CONTENT_STATS_TEST_ID}
            sx={[
              { py: 3.25, justifyContent: 'space-between' },
              styles.contentBox,
            ]}
          >
            {stats.map(({ icon, title, value }, i) => (
              <Stack
                direction="row"
                key={i}
                sx={{
                  width: `${100 / stats.length}%`,
                }}
              >
                <Box sx={styles.iconFrame}>{icon}</Box>
                <Stack
                  spacing={0.5}
                  sx={{
                    pl: 2,
                    pr: 3,
                  }}
                >
                  <Typography sx={styles.statName}>
                    {upperFirst(title)}
                  </Typography>
                  <Typography sx={styles.statValue}>
                    {upperFirst(value)}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
          <Box sx={[{ flexGrow: 1, py: 3.25 }, styles.contentBox]}>
            {!!inspirationContent?.implementation_of_action && (
              <ContentTextField
                content={inspirationContent.implementation_of_action}
                maxLines={10}
              />
            )}
          </Box>
          <Box sx={[{ flexGrow: 1, py: 3.25 }, styles.contentBox]}>
            {!!inspirationContent?.further_support && (
              <ContentTextField
                content={inspirationContent.further_support}
                maxLines={6}
              />
            )}
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default PanelContent;
